/**
 * Converts a ThemeOverrideConfig into a flat map of CSS custom properties.
 *
 * The resulting map can be applied as inline styles on a scoped wrapper element
 * so that all descendant components pick up the overridden design tokens.
 */

import type * as React from 'react';

import type { ThemeOverrideConfig } from '../types';
import {
  TYPOGRAPHY_KEY_MAP,
  RADIUS_KEY_MAP,
  SHADOW_KEY_MAP,
} from '../constants';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function applyMappedOverrides(
  map: Record<string, string>,
  section: object | undefined,
  keyMap: Record<string, string>,
): void {
  if (!section) return;
  for (const [key, value] of Object.entries(section)) {
    if (value == null || typeof value !== 'string') continue;
    const cssVar = keyMap[key];
    if (cssVar) map[cssVar] = value;
  }
}

function applyColorOverrides(
  map: Record<string, string>,
  colors: ThemeOverrideConfig['colors'],
): void {
  if (!colors) return;
  for (const [palette, scale] of Object.entries(colors)) {
    if (!scale) continue;
    for (const [shade, value] of Object.entries(scale)) {
      if (value == null || typeof value !== 'string') continue;
      map[`--color-${palette}-${shade}`] = value;
    }
  }
}

function applyTypographyOverrides(
  map: Record<string, string>,
  typography: ThemeOverrideConfig['typography'],
): void {
  if (!typography) return;
  applyMappedOverrides(map, typography, TYPOGRAPHY_KEY_MAP);
  // Apply font-family directly so the wrapper element picks up the override
  // (CSS custom property changes don't re-trigger inherited computed values)
  if (typography.fontSans) {
    map['fontFamily'] = `var(--font-sans)`;
  }
}

function applyCustomPropertyOverrides(
  map: Record<string, string>,
  customProperties: ThemeOverrideConfig['customProperties'],
): void {
  if (!customProperties) return;
  for (const [key, value] of Object.entries(customProperties)) {
    if (value == null) continue;
    map[key] = value;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface BuildThemeStyleResult {
  /** CSS custom properties cast as React.CSSProperties for inline style usage. */
  styleOverrides: React.CSSProperties;
  /** Raw flat map of CSS variable name → value (for context / merging). */
  overrideMap: Record<string, string>;
}

/**
 * Build a flat CSS custom-property map from a `ThemeOverrideConfig`.
 *
 * Only keys that are explicitly set in the config are emitted — missing
 * sections or individual shades are silently skipped.
 */
export function buildThemeStyle(
  config: ThemeOverrideConfig,
): BuildThemeStyleResult {
  const map: Record<string, string> = {};

  applyColorOverrides(map, config.colors);
  applyTypographyOverrides(map, config.typography);
  applyMappedOverrides(map, config.borderRadius, RADIUS_KEY_MAP);
  applyMappedOverrides(map, config.shadows, SHADOW_KEY_MAP);
  applyCustomPropertyOverrides(map, config.customProperties);

  return {
    styleOverrides: map as React.CSSProperties,
    overrideMap: map,
  };
}
